import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { ApiError, apiFetch } from '../api/client.ts';
import { useAuth } from '../auth/AuthContext.tsx';
import { Button } from '../components/Button.tsx';
import { Card } from '../components/Card.tsx';
import { FormField } from '../components/FormField.tsx';
import { Select } from '../components/Select.tsx';

interface IssuedEnrollment {
  enrollmentUrl: string;
  expiresInHours: number;
  emailSent: boolean;
  emailError: string | null;
}

interface AdminAgent {
  id: string;
  email: string;
  name: string;
  role: 'agent' | 'admin' | 'time_admin' | 'billing_admin' | 'quote_agent';
  isActive: boolean;
  isFullTime: boolean;
  phoneNumber: string | null;
  // YYYY-MM-DD. Drives PTO accrual (see /admin/pto).
  hiredOn: string | null;
  enrolled: boolean;
  lastLoginAt: string | null;
  createdAt: string;
}

export function AdminAgentsPage(): React.ReactElement {
  const { agent: me } = useAuth();
  const [agents, setAgents] = useState<AdminAgent[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [info, setInfo] = useState<string | null>(null);

  async function load() {
    try {
      const res = await apiFetch<{ agents: AdminAgent[] }>('/api/admin/agents');
      setAgents(res.agents);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Failed to load agents.');
    }
  }

  useEffect(() => {
    void load();
  }, []);

  if (!me) return <p>Loading…</p>;
  if (me.role !== 'admin') {
    return (
      <Card>
        <p className="text-[var(--color-muted)]">Admins only.</p>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <Link to="/admin" className="text-sm text-[var(--color-muted)] hover:underline">
            ← Admin
          </Link>
          <h1 className="mt-1 text-3xl font-bold text-[var(--color-header)]">Technicians</h1>
          <p className="mt-1 text-[var(--color-muted)]">Add and manage agent accounts.</p>
        </div>
        <Button onClick={() => setShowCreate((s) => !s)}>
          {showCreate ? 'Cancel' : 'New technician'}
        </Button>
      </div>

      {info && (
        <Card className="border-green-300 bg-green-50/40">
          <p className="break-all whitespace-pre-wrap text-sm text-green-900">{info}</p>
          <div className="mt-3 flex gap-2">
            {/* Enrollment links are long and single-use — retyping one off the
                screen is how they get mangled. */}
            {info.match(/https?:\/\/\S+/) && (
              <Button
                variant="outline"
                onClick={() => {
                  void navigator.clipboard.writeText(info.match(/https?:\/\/\S+/)![0]);
                }}
              >
                Copy link
              </Button>
            )}
            <Button variant="outline" onClick={() => setInfo(null)}>
              Dismiss
            </Button>
          </div>
        </Card>
      )}
      {error && (
        <Card className="border-[var(--color-danger)]/30 bg-red-50/40">
          <p className="text-sm text-[var(--color-danger)]">{error}</p>
        </Card>
      )}

      {showCreate && (
        <CreateForm
          onCreated={async (msg) => {
            setShowCreate(false);
            setInfo(msg);
            await load();
          }}
          onError={setError}
        />
      )}

      {agents === null ? (
        <p className="text-[var(--color-muted)]">Loading…</p>
      ) : (
        <div className="overflow-x-auto rounded-[var(--radius-card)] border border-[var(--color-border)] bg-white">
          {/* The inline editors (email, phone, start date) give every row a
              min-content width wider than a laptop viewport. This scrolls
              rather than clipping: the action buttons are in the last column
              and were being cut off with no way to reach them. */}
          <table className="w-full min-w-[64rem] text-sm">
            <thead className="bg-[var(--color-surface)] text-left text-xs uppercase tracking-wide text-[var(--color-muted)]">
              <tr>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Phone</th>
                <th className="px-4 py-3">Role</th>
                <th className="px-4 py-3">Employment</th>
                <th className="px-4 py-3">Start date</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Last login</th>
                {/* Pinned so the per-agent actions stay on screen however far
                    the row scrolls — they are the point of this page. */}
                <th className="sticky right-0 border-l border-[var(--color-border)] bg-[var(--color-surface)] px-4 py-3">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {agents.map((a) => (
                <AgentRow
                  key={a.id}
                  agent={a}
                  isMe={a.id === me.id}
                  onChanged={(msg) => {
                    if (msg) setInfo(msg);
                    void load();
                  }}
                  onError={setError}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function CreateForm({
  onCreated,
  onError,
}: {
  onCreated: (info: string) => void | Promise<void>;
  onError: (err: string) => void;
}): React.ReactElement {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<'agent' | 'admin' | 'time_admin' | 'billing_admin' | 'quote_agent'>('agent');
  const [sendEmail, setSendEmail] = useState(true);
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await apiFetch<{ enrollmentUrl: string; emailSent: boolean; emailError: string | null }>(
        '/api/admin/agents',
        { method: 'POST', json: { email, name, role, sendEmail } },
      );
      const lines = [`Created ${name} (${email}) as ${role}.`];
      if (res.emailSent) lines.push(`Enrollment email sent.`);
      else if (sendEmail) lines.push(`Email send failed (${res.emailError ?? 'unknown'}).`);
      lines.push(`Enrollment URL: ${res.enrollmentUrl}`);
      await onCreated(lines.join('\n'));
    } catch (e) {
      onError(e instanceof ApiError ? e.message : 'Failed to create technician.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <h2 className="text-base font-semibold text-[var(--color-header)]">New technician</h2>
      <form onSubmit={submit} className="mt-3 grid gap-3 sm:grid-cols-2">
        <FormField
          label="Name"
          name="name"
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <FormField
          label="Email"
          name="email"
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <Select label="Role" value={role} onChange={(e) => setRole(e.target.value as 'agent' | 'admin' | 'time_admin' | 'billing_admin' | 'quote_agent')}>
          <option value="agent">Agent</option>
          <option value="time_admin">Time admin</option>
          <option value="billing_admin">Billing admin</option>
          <option value="quote_agent">Quote agent</option>
          <option value="admin">Admin</option>
        </Select>
        <label className="flex items-end gap-2 text-sm">
          <input
            type="checkbox"
            checked={sendEmail}
            onChange={(e) => setSendEmail(e.target.checked)}
            className="h-4 w-4 accent-[var(--color-t3-blue)]"
          />
          Email the enrollment link
        </label>
        <div className="sm:col-span-2">
          <Button type="submit" disabled={busy || !email || !name}>
            {busy ? 'Creating…' : 'Create + issue enrollment'}
          </Button>
        </div>
      </form>
    </Card>
  );
}

function AgentRow({
  agent,
  isMe,
  onChanged,
  onError,
}: {
  agent: AdminAgent;
  isMe: boolean;
  onChanged: (info?: string) => void;
  onError: (err: string) => void;
}): React.ReactElement {
  const [busy, setBusy] = useState(false);
  const [emailDraft, setEmailDraft] = useState(agent.email);
  useEffect(() => {
    setEmailDraft(agent.email);
  }, [agent.email]);
  const [phoneDraft, setPhoneDraft] = useState(agent.phoneNumber ?? '');
  useEffect(() => {
    setPhoneDraft(agent.phoneNumber ?? '');
  }, [agent.phoneNumber]);
  const [hiredDraft, setHiredDraft] = useState(agent.hiredOn ?? '');
  useEffect(() => {
    setHiredDraft(agent.hiredOn ?? '');
  }, [agent.hiredOn]);

  async function patch(
    body: Partial<{
      email: string;
      name: string;
      role: 'agent' | 'admin' | 'time_admin' | 'billing_admin' | 'quote_agent';
      isActive: boolean;
      isFullTime: boolean;
      phoneNumber: string | null;
      hiredOn: string | null;
    }>,
  ) {
    setBusy(true);
    try {
      await apiFetch(`/api/admin/agents/${agent.id}`, { method: 'PATCH', json: body });
      onChanged();
    } catch (e) {
      onError(e instanceof ApiError ? e.message : 'Failed to update.');
    } finally {
      setBusy(false);
    }
  }

  // The email is the agent's login identity, so a change is confirmed before it
  // is sent, and the field snaps back to the stored value if it doesn't take.
  async function commitEmail() {
    const trimmed = emailDraft.trim();
    if (trimmed.toLowerCase() === agent.email.toLowerCase()) return;
    if (trimmed.length === 0) {
      setEmailDraft(agent.email);
      return;
    }
    if (!confirm(`Change ${agent.name}'s sign-in email from ${agent.email} to ${trimmed}?`)) {
      setEmailDraft(agent.email);
      return;
    }
    setBusy(true);
    try {
      await apiFetch(`/api/admin/agents/${agent.id}`, {
        method: 'PATCH',
        json: { email: trimmed },
      });
      onChanged(`${agent.name} now signs in as ${trimmed}.`);
    } catch (e) {
      setEmailDraft(agent.email);
      onError(e instanceof ApiError ? e.message : 'Failed to update email.');
    } finally {
      setBusy(false);
    }
  }

  async function commitPhone() {
    const trimmed = phoneDraft.trim();
    if (trimmed === (agent.phoneNumber ?? '')) return;
    await patch({ phoneNumber: trimmed.length === 0 ? null : trimmed });
  }

  async function commitHiredOn() {
    if (hiredDraft === (agent.hiredOn ?? '')) return;
    await patch({ hiredOn: hiredDraft.length === 0 ? null : hiredDraft });
  }

  // Both enrollment paths report the same way: whether the mail went out, and
  // the URL itself so an admin can hand it over directly when it didn't.
  function issuedMessage(res: IssuedEnrollment, lead: string): string {
    const lines = [lead];
    if (res.emailSent) lines.push(`Enrollment link emailed to ${agent.email}.`);
    else lines.push(`Email not sent (${res.emailError ?? 'unknown'}) — send this link yourself:`);
    lines.push(res.enrollmentUrl, `(Single-use, expires in ${res.expiresInHours}h.)`);
    return lines.join('\n');
  }

  async function reissue() {
    setBusy(true);
    try {
      const res = await apiFetch<IssuedEnrollment>(
        `/api/admin/agents/${agent.id}/reissue-enrollment`,
        { method: 'POST', json: { sendEmail: true } },
      );
      onChanged(issuedMessage(res, `New enrollment link issued for ${agent.name}.`));
    } catch (e) {
      onError(e instanceof ApiError ? e.message : 'Failed to re-issue.');
    } finally {
      setBusy(false);
    }
  }

  async function resetPassword() {
    if (!confirm(`Send a password reset link to ${agent.email}?`)) return;
    setBusy(true);
    try {
      await apiFetch(`/api/admin/agents/${agent.id}/reset-password`, { method: 'POST' });
      onChanged(`Password reset link emailed to ${agent.email}. Link is valid for 1 hour.`);
    } catch (e) {
      onError(e instanceof ApiError ? e.message : 'Reset failed.');
    } finally {
      setBusy(false);
    }
  }

  async function resetMfa() {
    if (
      !confirm(
        `Clear ${agent.email}'s TOTP and backup codes? They'll need to re-enroll an authenticator with the new link.`,
      )
    )
      return;
    setBusy(true);
    try {
      const res = await apiFetch<IssuedEnrollment>(`/api/admin/agents/${agent.id}/reset-mfa`, {
        method: 'POST',
        json: { sendEmail: true },
      });
      onChanged(issuedMessage(res, `Authenticator cleared for ${agent.name}.`));
    } catch (e) {
      onError(e instanceof ApiError ? e.message : 'Reset MFA failed.');
    } finally {
      setBusy(false);
    }
  }

  const rowClass = agent.isActive ? '' : 'bg-[var(--color-surface)]/40 text-[var(--color-muted)]';
  return (
    <tr className={`border-t border-[var(--color-border)] ${rowClass}`}>
      <td className="px-4 py-3">{agent.name}</td>
      <td className="px-4 py-3">
        <input
          type="email"
          value={emailDraft}
          onChange={(e) => setEmailDraft(e.target.value)}
          onBlur={() => void commitEmail()}
          onKeyDown={(e) => {
            if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
            if (e.key === 'Escape') setEmailDraft(agent.email);
          }}
          disabled={busy}
          title="Sign-in email — changing it changes how this agent logs in"
          className="h-9 w-56 rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-2 text-sm outline-none focus:border-[var(--color-t3-blue)] focus:ring-2 focus:ring-[var(--color-t3-blue)]/30 disabled:opacity-60"
        />
      </td>
      <td className="px-4 py-3">
        <input
          type="tel"
          value={phoneDraft}
          onChange={(e) => setPhoneDraft(e.target.value)}
          onBlur={() => void commitPhone()}
          onKeyDown={(e) => {
            if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
          }}
          placeholder="+1 555 123 4567"
          disabled={busy}
          className="h-9 w-40 rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-2 text-sm outline-none focus:border-[var(--color-t3-blue)] focus:ring-2 focus:ring-[var(--color-t3-blue)]/30 disabled:opacity-60"
        />
      </td>
      <td className="px-4 py-3">
        <Select
          value={agent.role}
          onChange={(e) => void patch({ role: e.target.value as 'agent' | 'admin' | 'time_admin' | 'billing_admin' | 'quote_agent' })}
          disabled={busy || isMe}
          className="min-w-[8.5rem]"
        >
          <option value="agent">Agent</option>
          <option value="time_admin">Time admin</option>
          <option value="billing_admin">Billing admin</option>
          <option value="quote_agent">Quote agent</option>
          <option value="admin">Admin</option>
        </Select>
      </td>
      <td className="px-4 py-3">
        <Select
          value={agent.isFullTime ? 'ft' : 'pt'}
          onChange={(e) => void patch({ isFullTime: e.target.value === 'ft' })}
          disabled={busy}
          className="min-w-[8.5rem]"
        >
          <option value="ft">Full-time</option>
          <option value="pt">Part-time</option>
        </Select>
      </td>
      <td className="px-4 py-3">
        <input
          type="date"
          value={hiredDraft}
          onChange={(e) => setHiredDraft(e.target.value)}
          onBlur={() => void commitHiredOn()}
          disabled={busy}
          title="Start date — PTO accrues one day per month from here"
          className="h-9 w-36 rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-2 text-sm outline-none focus:border-[var(--color-t3-blue)] focus:ring-2 focus:ring-[var(--color-t3-blue)]/30 disabled:opacity-60"
        />
      </td>
      <td className="px-4 py-3">
        {!agent.isActive ? (
          <span className="text-xs uppercase tracking-wide text-[var(--color-muted)]">disabled</span>
        ) : agent.enrolled ? (
          <span className="text-xs uppercase tracking-wide text-green-700">enrolled</span>
        ) : (
          <span className="text-xs uppercase tracking-wide text-amber-700">not enrolled</span>
        )}
      </td>
      <td className="px-4 py-3 text-xs text-[var(--color-muted)]">
        {agent.lastLoginAt ? new Date(agent.lastLoginAt).toLocaleString() : '—'}
      </td>
      <td className="sticky right-0 border-l border-[var(--color-border)] bg-white px-4 py-3 text-right">
        <div className="flex flex-wrap justify-end gap-2">
          {!agent.enrolled && agent.isActive && (
            <Button
              variant="outline"
              onClick={() => void reissue()}
              disabled={busy}
              title="Issue a fresh single-use enrollment link and email it to this technician"
            >
              Send enrollment link
            </Button>
          )}
          {agent.enrolled && agent.isActive && (
            <>
              <Button variant="outline" onClick={() => void resetPassword()} disabled={busy}>
                Reset pw
              </Button>
              <Button
                variant="outline"
                onClick={() => void resetMfa()}
                disabled={busy}
                title="Clear their authenticator and email a fresh enrollment link — the only way to re-enroll someone who is already set up"
              >
                Reset MFA
              </Button>
            </>
          )}
          {agent.isActive ? (
            <Button
              variant="outline"
              onClick={() => void patch({ isActive: false })}
              disabled={busy || isMe}
            >
              Disable
            </Button>
          ) : (
            <Button variant="outline" onClick={() => void patch({ isActive: true })} disabled={busy}>
              Enable
            </Button>
          )}
        </div>
      </td>
    </tr>
  );
}
